/**
 * Quién escribió último va arriba, y con cuántos mensajes que no viste.
 *
 * Lo pidió Rubén el 06-oct-2026 con WhatsApp al lado: ahí el que acaba de
 * escribir sube y lleva el número verde; en el portal la lista quedaba como
 * estaba y había que abrir cada conversación para saber si alguien había
 * escrito. Dos causas, las dos medidas:
 *
 * 1. **La lista venía ordenada por CREACIÓN.** El índice de conversaciones
 *    (`GSI1SK`) es la fecha en que nació el hilo, así que Kevin, que escribió a
 *    las 17:25, quedaba debajo de Silvia, que escribió a las 12:45 pero había
 *    entrado después. Se ordena por `last_inbound_ts`, que solo mueve el
 *    cliente: `last_message_ts` también lo mueve un recordatorio del bot, y la
 *    lista subiría conversaciones donde nadie dijo nada.
 *
 * 2. **No había de dónde sacar el número.** Ahora `mensajes_cliente` es el
 *    total de mensajes del cliente (lo suma la base, `conversation/
 *    _save_message`), y acá se guarda cuántos había cuando quien mira abrió la
 *    conversación. La diferencia es lo que no vio.
 *
 * Lo visto es de cada persona y vive en el SERVIDOR desde el 07-oct-2026
 * (`USER#{correo}` / `VISTO#{conversación}`, `operacion/vistos.py`): viaja con
 * la lista que el portal ya sondea y se guarda con `PUT /me/vistos`. Antes
 * vivía solo en el `localStorage` del navegador y otra computadora arrancaba
 * de cero; hoy ese `localStorage` es la caché.
 *
 * Todo lo que decide está acá y se prueba en Node
 * (`tests/unit/test_quien_escribio_va_arriba.py`,
 * `tests/unit/test_lo_visto_viaja_con_la_cuenta.py`); `useNoLeidos.ts` lo
 * cablea.
 */
import { primeroLasDeRevision, requiereRevisionCritica } from './atencion'

/** Lo mínimo para ordenar y contar. */
export interface ConMensajes {
  conversation_id: string
  last_inbound_ts?: string | null
  last_message_ts?: string | null
  updated_at?: string | null
  mensajes_cliente?: number | null
}

/** Cuántos mensajes del cliente había en cada conversación la última vez que
 *  quien mira la tuvo abierta. `null` = este navegador nunca guardó nada. */
export type Vistos = Record<string, number>

/**
 * Cuándo habló el cliente por última vez, para ordenar.
 *
 * Sin mensaje del cliente —una conversación que abrimos nosotros— cae a la
 * última actividad, para que no se vaya al fondo una que está pasando.
 */
export function ultimoDelCliente(c: ConMensajes): string {
  return c.last_inbound_ts || c.last_message_ts || c.updated_at || ''
}

/**
 * El orden de la lista: primero lo que requiere revisión por una alerta
 * crítica (DEF-039, como una conversación fijada en WhatsApp), y dentro de cada
 * grupo, quien escribió último arriba.
 *
 * Los dos `sort` son estables, así que el segundo respeta el orden del primero
 * entre las que empatan: no hace falta una comparación compuesta.
 */
export function ordenDeLaLista<T extends ConMensajes & Parameters<typeof requiereRevisionCritica>[0]>(
  convs: ReadonlyArray<T>,
): T[] {
  const porRecencia = [...convs].sort((a, b) => {
    const x = ultimoDelCliente(a)
    const y = ultimoDelCliente(b)
    return x === y ? 0 : x < y ? 1 : -1
  })
  return primeroLasDeRevision(porRecencia)
}

/** Cuántos mensajes del cliente no vio quien mira. */
export function noLeidos(c: ConMensajes, vistos: Vistos | null): number {
  if (!vistos) return 0
  const total = Number(c.mensajes_cliente) || 0
  return Math.max(0, total - (vistos[c.conversation_id] ?? 0))
}

/** Tope de conversaciones recordadas: cada una son unos 50 bytes. */
export const MAXIMO_RECORDADAS = 2000

/**
 * Lo visto, puesto al día con la lista que acaba de llegar.
 *
 * Tres reglas, y cada una evita un error que se ve en pantalla:
 *
 * - **La primera vez se toma nota de todo, sin números.** Sin nada guardado
 *   —el día que sale este cambio, o un navegador nuevo— cada conversación
 *   aparecería con todos sus mensajes como no leídos. Lo mismo que el sonido
 *   del Layout: lo que ya estaba no es una novedad.
 * - **Una conversación que no estaba, después, sí cuenta entera.** Es una que
 *   nació —o te asignaron— mientras no mirabas, y es justo la que hay que
 *   atender: tomarle nota en silencio la escondería.
 * - **Lo abierto se marca solo si alguien lo está mirando.** Con la pestaña
 *   oculta el mensaje que llega a la conversación abierta no lo vio nadie, y
 *   el número tiene que esperarlo hasta que vuelva.
 *
 * Si el contador de una conversación BAJÓ —se reinició para una prueba—, lo
 * visto baja con él: si no, quedaría «visto» un número que no se alcanza y los
 * mensajes nuevos no sumarían nunca.
 */
export function actualizarVistos(p: {
  vistos: Vistos | null
  conversaciones: ReadonlyArray<ConMensajes>
  abierta: string | null
  mirando: boolean
}): Vistos {
  const total = (c: ConMensajes) => Number(c.mensajes_cliente) || 0
  const siguiente: Vistos = { ...(p.vistos ?? {}) }
  for (const c of p.conversaciones) {
    const id = c.conversation_id
    if (!p.vistos) {
      siguiente[id] = total(c)
    } else if (id in siguiente && siguiente[id] > total(c)) {
      siguiente[id] = total(c)
    }
    if (p.mirando && id === p.abierta) siguiente[id] = total(c)
  }
  // Se olvidan las más viejas: las claves de un objeto conservan el orden de
  // inserción, y las que se tocan recién están al final.
  const claves = Object.keys(siguiente)
  for (const id of claves.slice(0, Math.max(0, claves.length - MAXIMO_RECORDADAS))) {
    delete siguiente[id]
  }
  return siguiente
}

/** ¿Cambió algo? Para no escribir `localStorage` en cada sondeo. */
export function mismosVistos(a: Vistos | null, b: Vistos): boolean {
  if (!a) return false
  const ka = Object.keys(a)
  if (ka.length !== Object.keys(b).length) return false
  return ka.every((k) => a[k] === b[k])
}

// ── Lo visto en el servidor ─────────────────────────────────────────────────

/** Lo visto según el servidor, como llega con la lista (`GET /conversations`). */
export interface VistosDelServidor {
  /** Lo guardado de las conversaciones listadas. Sin fila = nunca la vio. */
  vistos: Vistos
  /** false = esta persona nunca guardó nada: la primera vez se toma nota de todo. */
  iniciado: boolean
}

/** El cuerpo de `PUT /me/vistos`. */
export interface EnvioDeVistos {
  vistos: Vistos
  iniciar: boolean
}

/**
 * Lo visto, de la respuesta de la lista. `null` si el servidor no lo pudo
 * leer: se sigue con lo de este navegador, como antes del cambio, en vez de
 * mostrar como no leído lo que la persona ya vio.
 */
export function delServidor(
  res: { vistos?: unknown; vistos_iniciado?: unknown } | null | undefined,
): VistosDelServidor | null {
  const crudo = res?.vistos
  if (!crudo || typeof crudo !== 'object' || Array.isArray(crudo)) return null
  if (typeof res?.vistos_iniciado !== 'boolean') return null
  const vistos: Vistos = {}
  for (const [id, n] of Object.entries(crudo as Record<string, unknown>)) {
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0) vistos[id] = n
  }
  return { vistos, iniciado: res.vistos_iniciado }
}

/**
 * Lo visto con que se decide: lo de este navegador y lo del servidor, el mayor
 * de los dos en cada conversación.
 *
 * El mayor, y no el del servidor a secas: lo que este navegador acaba de
 * mandar puede no haber llegado cuando vuelve la lista siguiente, y el número
 * reaparecería un sondeo. Si el contador BAJÓ —una prueba reiniciada—,
 * `actualizarVistos` lo baja después.
 *
 * Mientras la persona no guardó nada en el servidor manda lo de este
 * navegador, que es lo que se sube: lo visto antes del cambio no se pierde.
 */
export function combinar(local: Vistos | null, servidor: VistosDelServidor | null): Vistos | null {
  if (!servidor?.iniciado) return local
  const juntos: Vistos = { ...(local ?? {}) }
  for (const [id, n] of Object.entries(servidor.vistos)) {
    juntos[id] = Math.max(juntos[id] ?? 0, n)
  }
  return juntos
}

/**
 * Lo que hay que guardar en el servidor: lo que difiere de lo que ya tiene,
 * solo de las conversaciones listadas (de las otras no sabe nada).
 *
 * - **Sin fila es lo mismo que 0 visto**, así que un 0 no se manda.
 * - **Lo mandado y todavía sin confirmar no se repite** (`enviados`): hasta la
 *   lista siguiente el servidor sigue mostrando lo de antes, y cada
 *   conversación que se abre en el medio lo volvería a mandar.
 * - **La primera vez va con `iniciar`, aunque no haya nada**: es la marca que
 *   distingue «nunca guardó» de «esta conversación es nueva para mí», y sin
 *   ella a quien entra con la bandeja vacía su primera asignación le llegaría
 *   sin número.
 */
export function porGuardar(
  siguiente: Vistos,
  servidor: VistosDelServidor,
  ids: ReadonlyArray<string>,
  enviados: Vistos,
): EnvioDeVistos | null {
  const vistos: Vistos = {}
  for (const id of ids) {
    if (!(id in siguiente)) continue
    const n = siguiente[id]
    if (n === (servidor.vistos[id] ?? 0) || enviados[id] === n) continue
    vistos[id] = n
  }
  if (servidor.iniciado && Object.keys(vistos).length === 0) return null
  return { vistos, iniciar: !servidor.iniciado }
}

/**
 * Un paso del número verde, entero: lo que queda visto y lo que hay que
 * guardar. `useNoLeidos` lo llama con cada lista y cada cambio de la
 * conversación abierta, y hace las escrituras.
 *
 * Sin `servidor` —todavía no llegó la lista, o no se pudo leer lo visto— se
 * decide con lo de este navegador y no se guarda nada.
 */
export function ponerAlDia(p: {
  local: Vistos | null
  servidor: VistosDelServidor | null
  conversaciones: ReadonlyArray<ConMensajes>
  abierta: string | null
  mirando: boolean
  enviados: Vistos
}): { vistos: Vistos; envio: EnvioDeVistos | null } {
  const vistos = actualizarVistos({
    vistos: combinar(p.local, p.servidor),
    conversaciones: p.conversaciones,
    abierta: p.abierta,
    mirando: p.mirando,
  })
  const ids = p.conversaciones.map((c) => c.conversation_id)
  return { vistos, envio: p.servidor ? porGuardar(vistos, p.servidor, ids, p.enviados) : null }
}

/**
 * Cómo se lee la hora de la fila, como en WhatsApp: la hora si es de hoy,
 * «Ayer», y si no la fecha. Antes era siempre la hora, y con la lista ordenada
 * por recencia una «12:45» de hace tres días se leía como de esta mañana.
 *
 * `cuando` es «ahora», y entra por parámetro para poder probarla; la zona
 * es la del navegador, que es la de quien mira.
 */
export function horaDeLaFila(ts: string | null | undefined, cuando?: Date | string | null): string {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const ahora = cuando ? new Date(cuando) : new Date()
  const mismoDia = (x: Date, y: Date) =>
    x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate()
  if (mismoDia(d, ahora)) {
    return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  }
  const ayer = new Date(ahora)
  ayer.setDate(ahora.getDate() - 1)
  if (mismoDia(d, ayer)) return 'Ayer'
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit' })
}
